import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { clearStoredAuth, refreshCentralSession } from '@/lib/authSession.js';

// Ported from teliance-chat-portal/src/contexts/AuthContext.jsx.
// localStorage token storage + login/logout/OAuth rotation. api.js imports
// the mutable authLogout/authRefresh so it can trigger them on a 401.

const AUTH_URL = import.meta.env.VITE_AUTH_URL || 'https://auth.teliance.com';

export let authLogout = () => {};
export let authRefresh = async () => null;

const AuthContext = createContext({
  token: null, user: null, isAuthenticated: false,
  login: () => {}, logout: () => {}, refreshToken: async () => {},
});

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const initialSyncStarted = useRef(false);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [user, setUser] = useState(() => {
    const email = localStorage.getItem('userEmail');
    const name = localStorage.getItem('userName');
    const role = localStorage.getItem('userRole');
    return email ? { email, name, role } : null;
  });

  const login = useCallback((newToken, userData, newRefreshToken = null) => {
    localStorage.setItem('token', newToken);
    if (newRefreshToken) localStorage.setItem('refreshToken', newRefreshToken);
    if (userData.email) localStorage.setItem('userEmail', userData.email);
    if (userData.name) localStorage.setItem('userName', userData.name);
    if (userData.role) localStorage.setItem('userRole', userData.role);
    setToken(newToken);
    setUser(userData);
  }, []);

  const refreshAccessToken = useCallback(async () => {
    const result = await refreshCentralSession({ force: true });
    if (result.status === 'ok') {
      setToken(result.token);
      setUser({ email: result.user.email, name: result.user.username, role: result.user.role });
      return result.token;
    }
    if (result.status === 'invalid') {
      setToken(null);
      setUser(null);
    }
    return null;
  }, []);

  const logout = useCallback(async () => {
    const t = localStorage.getItem('token');
    if (t) {
      try {
        await fetch(`${AUTH_URL}/logout`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` },
        });
      } catch (e) { console.error('Logout request failed:', e); }
    }
    clearStoredAuth();
    setToken(null); setUser(null);
    // ?manual=1 stops the auto-SSO bounce so logout sticks.
    window.location.href = '/login?manual=1';
  }, []);

  useEffect(() => { authLogout = logout; authRefresh = refreshAccessToken; }, [logout, refreshAccessToken]);

  // Keep the shared .teliance.com SSO cookie alive while Events is active.
  // The access token and tel_session both have a 15-minute TTL, so refresh at
  // five minutes and whenever the tab resumes. This also observes a global
  // logout because the centrally-revoked refresh token will be rejected.
  useEffect(() => {
    let revalidating = false;
    const revalidate = async (force = false) => {
      if (revalidating) return;
      if (!localStorage.getItem('token')) return;
      revalidating = true;
      try {
        const result = await refreshCentralSession({ force });
        if (result.status === 'invalid') window.location.href = '/login';
      } finally { revalidating = false; }
    };
    if (!initialSyncStarted.current) {
      initialSyncStarted.current = true;
      revalidate(true);
    }
    const interval = window.setInterval(() => revalidate(false), 5 * 60 * 1000);
    const onVisibility = () => { if (document.visibilityState === 'visible') revalidate(true); };
    const onPageShow = () => revalidate(true);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);

  // Keep React state in sync when api.js refreshes the token out-of-band.
  useEffect(() => {
    const handleRefreshed = (event) => {
      const { token: newToken, user: userData } = event.detail || {};
      if (newToken) setToken(newToken);
      if (userData) setUser({ email: userData.email, name: userData.username, role: userData.role });
    };
    const handleExpired = () => { setToken(null); setUser(null); };
    window.addEventListener('auth:refreshed', handleRefreshed);
    window.addEventListener('auth:expired', handleExpired);
    return () => {
      window.removeEventListener('auth:refreshed', handleRefreshed);
      window.removeEventListener('auth:expired', handleExpired);
    };
  }, []);

  const value = { token, user, isAuthenticated: !!token, login, logout, refreshToken: refreshAccessToken };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
